<template>
    <v-card v-if="data && data.show" class="pa-0" style="border-left: 4px solid var(--v-primary-base);">
        <v-card-title class="font-weight-bold py-2">
            <v-icon left color="primary" class="mr-1">mdi-home-clock-outline</v-icon>
            <span style="font-size: 15px;">{{ data.scheduled ? 'WFH today' : 'Your WFH day' }}</span>
            <v-spacer></v-spacer>
            <v-chip v-if="data.signed_in_at" x-small label color="purple" text-color="white" class="px-2">Work from Home</v-chip>
            <v-icon v-if="!loading" small class="ml-2" @click="getData">mdi-refresh</v-icon>
        </v-card-title>
        <v-progress-linear v-if="loading" indeterminate height="2"></v-progress-linear>
        <v-divider class="my-0"></v-divider>

        <v-card-text class="pt-3">
            <!-- A WFH day later this week (the coming week on Fri/Sat) -->
            <div v-if="!data.scheduled && data.week">
                Your WFH day {{ data.week.date > weekEnd ? 'next week' : 'this week' }}: <strong>{{ fmtLong(data.week.date) }}</strong>
            </div>

            <!-- Today: not yet signed in -->
            <template v-else-if="!data.signed_in_at">
                <div v-if="!data.approved" class="orange--text">
                    Today is a WFH day on your schedule, but your WFH plan isn't approved — please come to the office.
                </div>
                <div v-else-if="data.eligibility && !data.eligibility.eligible" class="orange--text">
                    You're not eligible for WFH this week ({{ data.eligibility.failed.map(ruleLabel).join(', ') }}) — please come to the office.
                </div>
                <!-- Coming to the office (shown below) or a move waiting: no sign-in -->
                <div v-else-if="data.sign_in_block">
                    <span v-if="!data.week || !data.week.forgone_at" class="orange--text">{{ data.sign_in_block }}</span>
                </div>
                <template v-else>
                    <div class="mb-2">Select <strong>Work From Home</strong> with your sign-in time ({{ data.window.sign_in_from }}–{{ data.window.sign_in_to }}).</div>
                    <div class="d-flex align-center" style="gap: 8px;">
                        <v-text-field v-model="signInTime" type="time" dense outlined hide-details label="Sign in"
                            :min="data.window.sign_in_from" :max="data.window.sign_in_to" style="max-width: 140px;"></v-text-field>
                        <v-btn color="primary" small :disabled="!data.can_sign_in || !signInTime" :loading="saving" @click="signIn">
                            Work from home
                        </v-btn>
                    </div>
                    <div v-if="!data.can_sign_in" class="caption grey--text mt-1">You can sign in from {{ data.window.sign_in_from }}.</div>
                </template>
            </template>

            <!-- Signed in -->
            <template v-else>
                <div>
                    Signed in <strong>{{ data.signed_in_at }}</strong>
                    <span v-if="data.signed_out_at"> · signed out <strong>{{ data.signed_out_at }}</strong></span>
                </div>
                <div v-if="data.figures" class="caption mt-1">
                    Worked {{ fmtHours(data.figures.actual_worked) }}
                    <span v-if="data.figures.required_work_minutes"> of {{ fmtHours(data.figures.required_work_minutes) }} required</span>
                    <v-icon v-if="data.figures.required_work_met" x-small color="green darken-2">mdi-check-circle</v-icon>
                </div>
                <div v-if="!data.signed_out_at" class="mt-3">
                    <div class="mb-2">Sign out from {{ data.window.sign_out_from }} once you've finished.</div>
                    <div class="d-flex align-center" style="gap: 8px;">
                        <v-text-field v-model="signOutTime" type="time" dense outlined hide-details label="Sign out"
                            :min="data.window.sign_out_from" style="max-width: 140px;"></v-text-field>
                        <v-btn color="primary" small outlined :disabled="!data.can_sign_out || !signOutTime" :loading="saving" @click="signOut">
                            Sign out
                        </v-btn>
                    </div>
                </div>
                <div v-else class="caption grey--text mt-2">Your attendance updates within a minute or two — refresh to see the final hours.</div>
            </template>

            <!-- Change the day, or come to the office instead -->
            <wfh-day-actions v-if="data.week" class="mt-2" :entry-id="data.week.id" :date="data.week.date" :day="data.week" detailed @changed="getData" />
        </v-card-text>
    </v-card>
</template>

<script>
// The WFH day itself: on an approved, eligible WFH day the staff member signs
// in (07:30–08:00) and out (from 14:30) for a Work From Home entry of their
// approved monthly schedule. Rules, windows and worked hours all
// come from the backend (WfhDayService / AttendanceDayService); this only shows them.
// Also the week's approved WFH day ahead of it (the coming week's on Fri/Sat):
// ask to move it within the week, or tell HR you're coming to the office
// (WfhDayActions). Emits `loaded` with true when there's something to show
// (`show`), so the dashboard shows the column only then.
import WfhDayActions from '../monthly-schedule/WfhDayActions.vue'

export default {
    name: 'WfhDayCard',
    components: { WfhDayActions },
    data: () => ({
        data: null,
        loading: false,
        saving: false,
        signInTime: '',
        signOutTime: '',
    }),
    computed: {
        /** Thursday of the week under way: a WFH day after it is next week's. */
        weekEnd() {
            const d = new Date(`${this.data.date}T00:00:00`)
            d.setDate(d.getDate() - d.getDay() + 4)
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        },
    },
    mounted() {
        this.getData()
    },
    methods: {
        getData() {
            this.loading = true
            this.$axios.get('/api/monthly-schedule/today', { silentError: true }).then(resp => {
                this.data = resp.data
                this.prefill()
            }).catch(() => {
                this.data = null
            }).finally(() => {
                this.loading = false
                this.$emit('loaded', !!(this.data && this.data.show))
            })
        },
        /** Default to now, kept inside the allowed window. */
        prefill() {
            if (!this.data || !this.data.window) return
            const now = new Date()
            const hhmm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
            const w = this.data.window
            if (!this.signInTime) this.signInTime = hhmm < w.sign_in_from ? w.sign_in_from : (hhmm > w.sign_in_to ? w.sign_in_to : hhmm)
            if (!this.signOutTime) this.signOutTime = hhmm < w.sign_out_from ? w.sign_out_from : hhmm
        },
        signIn() {
            this.saving = true
            this.$axios.post('/api/monthly-schedule/today/sign-in', { time: this.signInTime })
                .then(resp => { this.data = resp.data.data })
                .finally(() => this.saving = false)
        },
        signOut() {
            this.saving = true
            this.$axios.post('/api/monthly-schedule/today/sign-out', { time: this.signOutTime })
                .then(resp => { this.data = resp.data.data })
                .finally(() => this.saving = false)
        },
        ruleLabel(key) {
            return {
                no_leave: 'leave last week',
                on_time: 'late or absent last week',
                min_hours: 'short hours last week',
                no_holiday: 'public holiday this week',
                service_year: 'less than one year of service',
            }[key] || key
        },
        fmtLong(s) {
            return new Date(`${s}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
        },
        fmtHours(mins) {
            return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`
        },
    },
}
</script>
